// Cycle de vie d'un incident — 13 statuts (chapitre 10.3 du dossier).
// Toute transition est historisée avec son auteur (IncidentStatusHistory).

export const INCIDENT_STATUSES = [
  'REPORTED', //            signalé
  'QUALIFYING', //          en cours de qualification
  'CONFIRMED', //           confirmé
  'NOT_CONFIRMED', //       non confirmé
  'ASSIGNED', //            assigné
  'WAITING_RESOURCES', //   en attente de ressources
  'IN_PROGRESS', //         intervention en cours
  'REPAIRED', //            réparation effectuée
  'PENDING_VERIFICATION', //en attente de vérification
  'RESTORED_VERIFIED', //   rétabli et vérifié
  'CLOSED', //              clôturé
  'REOPENED', //            rouvert
  'CANCELLED', //           annulé avec justification
] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  REPORTED: 'Signalé',
  QUALIFYING: 'En cours de qualification',
  CONFIRMED: 'Confirmé',
  NOT_CONFIRMED: 'Non confirmé',
  ASSIGNED: 'Assigné',
  WAITING_RESOURCES: 'En attente de ressources',
  IN_PROGRESS: 'Intervention en cours',
  REPAIRED: 'Réparation effectuée',
  PENDING_VERIFICATION: 'En attente de vérification',
  RESTORED_VERIFIED: 'Rétabli et vérifié',
  CLOSED: 'Clôturé',
  REOPENED: 'Rouvert',
  CANCELLED: 'Annulé (justifié)',
};

/** Traduction simplifiée présentée au public (évite toute confusion). */
export const PUBLIC_STATUS: Record<IncidentStatus, string> = {
  REPORTED: 'Signalé — en cours de vérification',
  QUALIFYING: 'Signalé — en cours de vérification',
  CONFIRMED: 'Confirmé',
  NOT_CONFIRMED: 'Non confirmé',
  ASSIGNED: 'Intervention prévue',
  WAITING_RESOURCES: 'Intervention prévue',
  IN_PROGRESS: 'Intervention en cours',
  REPAIRED: 'Rétablissement en cours de vérification',
  PENDING_VERIFICATION: 'Rétablissement en cours de vérification',
  RESTORED_VERIFIED: 'Rétabli',
  CLOSED: 'Rétabli',
  REOPENED: 'Confirmé — problème persistant',
  CANCELLED: 'Clôturé sans suite',
};

/** Statuts considérés comme « actifs » (incident ouvert). */
export const ACTIVE_STATUSES: IncidentStatus[] = [
  'REPORTED',
  'QUALIFYING',
  'CONFIRMED',
  'ASSIGNED',
  'WAITING_RESOURCES',
  'IN_PROGRESS',
  'REPAIRED',
  'PENDING_VERIFICATION',
  'REOPENED',
];

/** Actifs ET confirmés (exclut les incidents encore en qualification). */
export const CONFIRMED_ACTIVE_STATUSES: IncidentStatus[] = [
  'CONFIRMED',
  'ASSIGNED',
  'WAITING_RESOURCES',
  'IN_PROGRESS',
  'REPAIRED',
  'PENDING_VERIFICATION',
  'REOPENED',
];

export const TERMINAL_STATUSES: IncidentStatus[] = ['CLOSED', 'NOT_CONFIRMED', 'CANCELLED'];

type Rule = {
  to: IncidentStatus;
  requiresComment?: boolean;
  permission: 'incidents.transition' | 'incidents.validate_closure';
};

/**
 * Transitions autorisées. Les transitions de validation (vérification,
 * clôture) exigent un niveau distinct de celui de l'intervention.
 */
export const TRANSITIONS: Record<IncidentStatus, Rule[]> = {
  REPORTED: [
    { to: 'QUALIFYING', permission: 'incidents.transition' },
    { to: 'CONFIRMED', permission: 'incidents.transition' },
    { to: 'NOT_CONFIRMED', requiresComment: true, permission: 'incidents.transition' },
    { to: 'CANCELLED', requiresComment: true, permission: 'incidents.transition' },
  ],
  QUALIFYING: [
    { to: 'CONFIRMED', permission: 'incidents.transition' },
    { to: 'NOT_CONFIRMED', requiresComment: true, permission: 'incidents.transition' },
    { to: 'CANCELLED', requiresComment: true, permission: 'incidents.transition' },
  ],
  CONFIRMED: [
    { to: 'ASSIGNED', permission: 'incidents.transition' },
    { to: 'WAITING_RESOURCES', requiresComment: true, permission: 'incidents.transition' },
    { to: 'CANCELLED', requiresComment: true, permission: 'incidents.transition' },
  ],
  NOT_CONFIRMED: [{ to: 'REOPENED', requiresComment: true, permission: 'incidents.transition' }],
  ASSIGNED: [
    { to: 'IN_PROGRESS', permission: 'incidents.transition' },
    { to: 'WAITING_RESOURCES', requiresComment: true, permission: 'incidents.transition' },
    { to: 'CONFIRMED', requiresComment: true, permission: 'incidents.transition' },
  ],
  WAITING_RESOURCES: [
    { to: 'ASSIGNED', permission: 'incidents.transition' },
    { to: 'IN_PROGRESS', permission: 'incidents.transition' },
    { to: 'CANCELLED', requiresComment: true, permission: 'incidents.transition' },
  ],
  IN_PROGRESS: [
    { to: 'REPAIRED', permission: 'incidents.transition' },
    { to: 'WAITING_RESOURCES', requiresComment: true, permission: 'incidents.transition' },
  ],
  REPAIRED: [
    { to: 'PENDING_VERIFICATION', permission: 'incidents.transition' },
    { to: 'RESTORED_VERIFIED', permission: 'incidents.validate_closure' },
    { to: 'IN_PROGRESS', requiresComment: true, permission: 'incidents.validate_closure' },
  ],
  PENDING_VERIFICATION: [
    { to: 'RESTORED_VERIFIED', permission: 'incidents.validate_closure' },
    { to: 'IN_PROGRESS', requiresComment: true, permission: 'incidents.validate_closure' },
  ],
  RESTORED_VERIFIED: [
    { to: 'CLOSED', requiresComment: true, permission: 'incidents.validate_closure' },
    { to: 'REOPENED', requiresComment: true, permission: 'incidents.transition' },
  ],
  CLOSED: [{ to: 'REOPENED', requiresComment: true, permission: 'incidents.transition' }],
  REOPENED: [
    { to: 'CONFIRMED', permission: 'incidents.transition' },
    { to: 'ASSIGNED', permission: 'incidents.transition' },
    { to: 'CANCELLED', requiresComment: true, permission: 'incidents.transition' },
  ],
  CANCELLED: [{ to: 'REOPENED', requiresComment: true, permission: 'incidents.transition' }],
};

export function findTransition(from: string, to: string): Rule | undefined {
  return TRANSITIONS[from as IncidentStatus]?.find((r) => r.to === to);
}

/** Champ horodaté mis à jour lors de l'entrée dans un statut. */
export const STATUS_TIMESTAMP: Partial<Record<IncidentStatus, string>> = {
  QUALIFYING: 'qualifiedAt',
  CONFIRMED: 'confirmedAt',
  ASSIGNED: 'assignedAt',
  IN_PROGRESS: 'interventionStartedAt',
  REPAIRED: 'repairedAt',
  RESTORED_VERIFIED: 'restoredAt',
  CLOSED: 'closedAt',
};
